import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Wallet, IndianRupee, Calendar, CheckCircle,
  Clock, TrendingDown
} from 'lucide-react';
import MemberLayout from '../../components/layouts/MemberLayout';
import { apiClient } from '../../App';
import { toast } from 'sonner';
import { Progress } from '../../components/ui/progress';

const MemberLoans = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMemberData();
  }, []);

  const fetchMemberData = async () => {
    try {
      const response = await apiClient.get('/dashboard/member-stats');
      setData(response.data);
    } catch (error) {
      toast.error('Failed to fetch loan data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <MemberLayout title="My Loans">
        <div className="space-y-6">
          <div className="dashboard-card p-6">
            <div className="skeleton h-48 rounded-xl"></div>
          </div>
        </div>
      </MemberLayout>
    );
  }

  const activeLoan = data?.active_loan;
  const allLoans = data?.loans || [];
  const emis = data?.emis || [];

  const loanProgress = activeLoan ? 
    (activeLoan.paid_emis / (activeLoan.paid_emis + activeLoan.pending_emis)) * 100 : 0;

  return (
    <MemberLayout title="My Loans" subtitle="Track your loan status and EMI payments">
      {/* Active Loan Card */}
      {activeLoan ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="dashboard-card p-8 mb-8 border-l-4 border-[#2962FF]"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-[#2962FF]/10 flex items-center justify-center">
              <Wallet className="w-6 h-6 text-[#2962FF]" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-[#0A2540]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Active Loan
              </h3>
              <p className="text-sm text-[#425466]">
                Started on {new Date(activeLoan.disbursement_date).toLocaleDateString('en-IN')}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
            <div className="p-4 rounded-xl bg-[#F4F7FB]">
              <p className="text-sm text-[#425466] mb-1">Principal Amount</p>
              <p className="text-xl font-bold text-[#0A2540]">{formatCurrency(activeLoan.amount)}</p>
            </div>
            <div className="p-4 rounded-xl bg-[#F4F7FB]">
              <p className="text-sm text-[#425466] mb-1">Interest Rate</p>
              <p className="text-xl font-bold text-[#0A2540]">{activeLoan.interest_rate}% / month</p>
            </div>
            <div className="p-4 rounded-xl bg-[#F4F7FB]">
              <p className="text-sm text-[#425466] mb-1">Monthly EMI</p>
              <p className="text-xl font-bold text-[#00C853]">{formatCurrency(activeLoan.emi_amount)}</p>
            </div>
            <div className="p-4 rounded-xl bg-[#F4F7FB]">
              <p className="text-sm text-[#425466] mb-1">Remaining Amount</p>
              <p className="text-xl font-bold text-[#2962FF]">{formatCurrency(activeLoan.remaining_amount)}</p>
            </div>
          </div>

          {/* Progress */}
          <div className="mb-6">
            <div className="flex justify-between mb-2">
              <span className="text-sm font-medium text-[#0A2540]">Repayment Progress</span>
              <span className="text-sm text-[#425466]">
                {activeLoan.paid_emis} of {activeLoan.paid_emis + activeLoan.pending_emis} EMIs paid ({Math.round(loanProgress)}%)
              </span>
            </div>
            <Progress value={loanProgress} className="h-4" />
          </div>

          {/* Summary Row */}
          <div className="grid grid-cols-3 gap-4 pt-6 border-t border-gray-100">
            <div className="text-center">
              <p className="text-sm text-[#425466]">Total Interest</p>
              <p className="text-lg font-bold text-orange-600">{formatCurrency(activeLoan.total_interest)}</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-[#425466]">Paid Amount</p>
              <p className="text-lg font-bold text-green-600">{formatCurrency(activeLoan.paid_amount)}</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-[#425466]">Total Amount</p>
              <p className="text-lg font-bold text-[#0A2540]">{formatCurrency(activeLoan.total_amount)}</p>
            </div>
          </div>
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="dashboard-card p-12 text-center mb-8"
        >
          <CheckCircle className="w-20 h-20 mx-auto mb-6 text-[#00C853]" />
          <h3 className="text-xl font-semibold text-[#0A2540] mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
            No Active Loan
          </h3>
          <p className="text-[#425466]">You don't have any active loans. Contact admin for loan requests.</p>
        </motion.div>
      )}

      {/* EMI Schedule */}
      {emis.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="dashboard-card overflow-hidden mb-8"
        >
          <div className="p-6 border-b border-gray-100">
            <h3 className="text-lg font-semibold text-[#0A2540]" style={{ fontFamily: 'Poppins, sans-serif' }}>
              EMI Schedule
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-[#F4F7FB]">
                  <th className="px-6 py-4 text-left text-xs font-semibold text-[#425466] uppercase">EMI #</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-[#425466] uppercase">Due Date</th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-[#425466] uppercase">EMI Amount</th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-[#425466] uppercase">Principal</th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-[#425466] uppercase">Interest</th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-[#425466] uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {emis.map((emi, index) => (
                  <tr 
                    key={emi.id} 
                    className={`hover:bg-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}
                  >
                    <td className="px-6 py-4">
                      <span className="font-semibold text-[#0A2540]">#{emi.emi_number}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-[#425466]" />
                        <span className="text-[#0A2540]">
                          {new Date(emi.due_date).toLocaleDateString('en-IN', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span className="font-semibold text-[#0A2540]">{formatCurrency(emi.amount)}</span>
                    </td>
                    <td className="px-6 py-4 text-right text-[#425466]">
                      {formatCurrency(emi.principal)}
                    </td>
                    <td className="px-6 py-4 text-right text-[#425466]">
                      {formatCurrency(emi.interest)}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${
                        emi.status === 'paid' ? 'bg-green-100 text-green-600' :
                        emi.status === 'overdue' ? 'bg-red-100 text-red-600' :
                        'bg-yellow-100 text-yellow-600'
                      }`}>
                        {emi.status === 'paid' ? <CheckCircle className="w-3 h-3" /> :
                         emi.status === 'overdue' ? <TrendingDown className="w-3 h-3" /> :
                         <Clock className="w-3 h-3" />}
                        {emi.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      )}

      {/* Loan History */}
      {allLoans.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="dashboard-card overflow-hidden"
        >
          <div className="p-6 border-b border-gray-100">
            <h3 className="text-lg font-semibold text-[#0A2540]" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Loan History
            </h3>
          </div>
          <div className="divide-y divide-gray-100">
            {allLoans.map((loan) => (
              <div key={loan.id} className="p-6 flex items-center justify-between hover:bg-gray-50">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    loan.status === 'active' ? 'bg-green-100' :
                    loan.status === 'closed' ? 'bg-gray-100' :
                    loan.status === 'pending' ? 'bg-yellow-100' :
                    'bg-red-100'
                  }`}>
                    <Wallet className={`w-6 h-6 ${
                      loan.status === 'active' ? 'text-green-600' :
                      loan.status === 'closed' ? 'text-gray-600' :
                      loan.status === 'pending' ? 'text-yellow-600' :
                      'text-red-600'
                    }`} />
                  </div>
                  <div>
                    <p className="font-semibold text-[#0A2540]">{formatCurrency(loan.amount)}</p>
                    <p className="text-sm text-[#425466]">
                      {loan.tenure_months} months @ {loan.interest_rate}%
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    loan.status === 'active' ? 'bg-green-100 text-green-600' :
                    loan.status === 'closed' ? 'bg-gray-100 text-gray-600' :
                    loan.status === 'pending' ? 'bg-yellow-100 text-yellow-600' :
                    'bg-red-100 text-red-600'
                  }`}>
                    {loan.status}
                  </span>
                  {loan.disbursement_date && (
                    <p className="text-xs text-[#425466] mt-1">
                      {new Date(loan.disbursement_date).toLocaleDateString('en-IN')}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </MemberLayout>
  );
};

export default MemberLoans;
